'use client';

import {
  getActionAriaLabel,
  getConstantAriaLabel,
  getFormAriaLabel,
  getLinkAriaLabel,
} from '@/lib/a11y/ariaLabels';
import { getBrandDisplayName } from '@/lib/cms/config';
import { useAriaDictionary } from '@/providers/ariaLabelsProvider';

/**
 * Client hook for resolving aria-labels. Native-architecture replacement for the
 * legacy `useAriaLabels` — synchronous (dictionary is already in context) and
 * brand-aware. Mirrors the legacy getter API.
 *
 * @example
 * const aria = useAriaLabels();
 * <Link aria-label={aria.link(href)} />
 * <button aria-label={aria.action('close_modal')} />
 */
export function useAriaLabels() {
  const dict = useAriaDictionary();
  const brand = getBrandDisplayName();

  return {
    /** Aria-label for a link, from the URL map with a derived fallback. */
    link: (href: string) => getLinkAriaLabel(href, dict, brand),
    /** Aria-label for a keyed action (e.g. 'close_modal'). */
    action: (key: string) => getActionAriaLabel(dict, key, brand),
    /** Aria-label for a keyed form field. */
    form: (key: string) => getFormAriaLabel(dict, key, brand),
    /** Aria-label for a keyed constant URL. */
    constant: (key: string) => getConstantAriaLabel(dict, key, brand),
  };
}
